import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Sparkles, CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { refreshToken } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const polledRef = useRef(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && !polledRef.current) {
      polledRef.current = true;
      pollPaymentStatus(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const { data } = await axios.get(`${API_URL}/api/checkout/status/${sid}`, {
        withCredentials: true
      });

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        await refreshToken();
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Poll error:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-midnight flex flex-col">
      <SiteHeader />
      <div className="flex-1 flex items-center justify-center px-6 relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-aura-purple/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-aura-gold/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 max-w-md w-full rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 text-center"
        data-testid="payment-result-card"
      >
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-xl font-serif text-text-light">Memouraa</span>
        </Link>

        {(status === 'checking' || status === 'processing') && (
          <div data-testid="payment-processing">
            <Loader2 className="w-16 h-16 text-champagne mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-serif font-bold text-text-light mb-2">Processing Payment</h2>
            <p className="text-text-muted">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success">
            <CheckCircle className="w-16 h-16 text-success mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-bold text-text-light mb-2">Payment Successful!</h2>
            <p className="text-text-muted mb-2">
              Your <span className="text-champagne font-semibold capitalize">{paymentData?.plan}</span> plan is now active.
            </p>
            {paymentData?.billing_cycle && (
              <p className="text-text-muted text-sm mb-6">
                Billed {paymentData.billing_cycle}
              </p>
            )}
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-champagne text-midnight font-semibold hover:bg-champagne-light transition-colors"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div data-testid="payment-failed">
            <XCircle className="w-16 h-16 text-error mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-bold text-text-light mb-2">
              {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
            </h2>
            <p className="text-text-muted mb-6">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'We could not confirm your payment. If you were charged, please contact support.'}
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-champagne/30 text-champagne font-semibold hover:bg-champagne/10 transition-colors"
              data-testid="retry-payment-btn"
            >
              Try Again
            </Link>
          </div>
        )}
      </motion.div>
      </div>
      <FooterExpanded />
    </div>
  );
}
